import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ArrowLeft, Newspaper, Share2 } from "lucide-react";
import api from "@/lib/api";
import { shareArticle } from "@/lib/share";

const EASE = [0.22, 1, 0.36, 1];

export default function NewsArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/news/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => setMissing(true));
  }, [id]);

  return (
    <main className="pt-28 pb-24 bg-league-mist min-h-screen" data-testid="news-article-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between mb-8">
          <Link to="/news" data-testid="article-back" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-league-blue transition-colors hover:text-league-red">
            <ArrowLeft size={14} /> All stories
          </Link>
          {article && (
            <button
              onClick={() => shareArticle({ title: article.title, text: article.body.slice(0, 120), path: `/news/${article.id}`, ogPath: `/api/og/news/${article.id}` })}
              data-testid="article-share"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-league-blue text-white text-xs font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-league-deep cursor-pointer"
            >
              <Share2 size={13} /> Share
            </button>
          )}
        </div>

        {!article && !missing && <p className="text-muted-foreground" data-testid="article-loading">Loading story…</p>}
        {missing && (
          <p className="text-muted-foreground" data-testid="article-missing">This story doesn't exist anymore — browse all stories instead.</p>
        )}

        {article && (
          <motion.article
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
            data-testid={`article-${article.id}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-league-blue text-white text-[11px] font-bold uppercase tracking-widest">{article.tag}</span>
              <span className="text-xs text-muted-foreground font-semibold">{format(new Date(article.published_at), "dd MMM yyyy")}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-league-ink tracking-tighter leading-tight mb-8" data-testid="article-title">
              {article.title}
            </h1>
            {article.image_url && (
              <img src={article.image_url} alt={article.title} className="w-full rounded-2xl border border-black/5 object-cover mb-8" data-testid="article-cover" />
            )}
            <p className="text-league-ink/80 leading-relaxed text-base sm:text-lg whitespace-pre-line" data-testid="article-body">
              {article.body}
            </p>
            {article.image_url_2 && (
              <img src={article.image_url_2} alt={`${article.title} — more`} className="mt-8 w-full max-w-md rounded-2xl border border-black/5 object-cover" data-testid="article-image-2" />
            )}
            <div className="mt-10 pt-6 border-t border-black/10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => shareArticle({ title: article.title, text: article.body.slice(0, 120), path: `/news/${article.id}`, ogPath: `/api/og/news/${article.id}` })}
                data-testid="article-share-bottom"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-league-blue text-white text-xs font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-league-deep cursor-pointer"
              >
                <Share2 size={14} /> Share this story
              </button>
              <span className="text-xs text-muted-foreground flex items-center gap-2">
                <Newspaper size={13} /> All Star League — League Wire
              </span>
            </div>
          </motion.article>
        )}
      </div>
    </main>
  );
}
